import { useCallback, useContext } from "react";
import { useDispatch } from "react-redux";

import { Events, ModelSelectors, UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel, FormModelPath } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import {
	DefaultWidgetMap,
	FormModelMap,
	useDocumentPathForInput
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { localizableFromModel } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";

import { TEXTBAUSTEINE_ANZEIGEN, EMAIL_VERSAND } from "../../../utilities/variables/modulfRuntimeAnnotations";

import { openTextTree } from "../sagas/actions";

interface BescheidErstellenButtonsProps {
	readonly activityId: string;
	readonly inlineProps: FormModelMap.FormModelComponentProps<FormModel.InlineRepeat>;
	readonly fvKey: string;
	readonly disabled?: boolean;
	readonly kategorie?: string;
}

export function BescheidErstellenButtons(props: BescheidErstellenButtonsProps) {
	const {
		activityId,
		inlineProps: { config, modelElement },
		fvKey,
		disabled,
		kategorie
	} = props;

	const dispatch = useDispatch();
	const documentModel = ModelSelectors.documentModel()(config.renderOptions.state);
	const uiDisabled = UiStateSelectors.disabled()(config.renderOptions.state);

	const { groupPath, annotations } = modelElement;

	const parentPath = config.parentPath;

	const path = useDocumentPathForInput(groupPath, documentModel);
	const repeatFormModelPath = FormModelPath.extend(parentPath, modelElement);

	const formModel = config.renderOptions.state.models.formModel;
	const { localizer } = useContext(LocalizerContext);

	const showTextbausteinAnnotation = annotations?.find(it => it.name === TEXTBAUSTEINE_ANZEIGEN);
	const hasTextbausteinButton = showTextbausteinAnnotation ? showTextbausteinAnnotation.value === "true" : false;

	const addLabel = localizer(
		localizableFromModel(
			`uiModel.${formModel.header.id}.defaults.buttonLabel.add`,
			modelElement.buttonLabels?.ADD?.text ?? [{ locale: "de", text: "Hinzufügen" }]
		)
	);

	const addRow = useCallback(() => {
		dispatch(
			FormEngineActions.command({
				activityId,
				engineEvent: Events.Repeat.addRow({ path: path, repeatFormModelPath })
			})
		);
	}, [dispatch, activityId, path, repeatFormModelPath]);

	const openTextbausteine = useCallback(() => {
		const targetFieldName = modelElement.repeatOverviewColumn
			?.flatMap(col => (col as { annotations?: { name: string; value: string }[] }).annotations ?? [])
			.find(a => a.name === EMAIL_VERSAND)?.value;
		dispatch(
			openTextTree({
				initiatingActivityId: activityId,
				fvKey,
				repeatableGroupPath: path,
				kategorie,
				targetFieldName
			})
		);
	}, [activityId, dispatch, fvKey, path, kategorie, modelElement.repeatOverviewColumn]);

	return (
		<DefaultWidgetMap.ButtonGroup>
			<DefaultWidgetMap.Button
				label={addLabel || "Neuer Text"}
				icon={<Icon>add</Icon>}
				disabled={disabled || uiDisabled}
				onClick={addRow}
			/>
			{hasTextbausteinButton && (
				<DefaultWidgetMap.Button
					label="Textbausteine"
					icon={<Icon>library_books</Icon>}
					disabled={disabled || uiDisabled}
					onClick={openTextbausteine}
				/>
			)}
		</DefaultWidgetMap.ButtonGroup>
	);
}
